// City-state rings: a Civ-style ring on the capital hex of every country flagged city_state, drawn
// even where the state owns no hex (Vatican, Monaco, San Marino…): a hex-scale ring says "a state
// stands here", which its border cannot.
//
//        ╭ ─ ─ ─ ╮       outer ring   primary colour, casing lighter
//      ╱ ╵ ╵ ╵ ╵ ╵ ╲     ticks        secondary colour, between the rings
//     │  ─────────  │    inner ring   secondary colour
//      ╲ ╷ ╷ ╷ ╷ ╷ ╱     disc         faint primary tint
//        ╰ ─ ─ ─ ╯
//
// The ring plan comes from ActorLabel (cityState + capitalHex); the shader reads the state's actor
// index per hex from the political atlas (claims.ts ringAt). Truth: a ring stands on the capital
// hex the country record names; a state whose record has no sources counts as an unsourced marker.

import { atan, float, length, mix, smoothstep, fract, abs, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { HEX_COUNT } from '../../core/grid.ts';
import type { SourceId } from '../../core/schema.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import type { ActorLabel } from './labels.ts';
import type { ClaimField } from './claims.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** A ring to draw: whose, where, and the sources of the country record that says so. */
export interface CityStateRing {
  /** Actor index of the city-state. */
  index: number;
  /** Hex of its capital. */
  hex: number;
  name: string;
  sources: SourceId[];
}

// Ring geometry in hex radii (a hex centre-to-edge is 0.866): kept inside the hex so rings of
// neighbouring hexes never touch.
const OUTER_R = 0.7;
const OUTER_W = 0.045;
const OUTER_MIN_PX = 1.8;
const INNER_R = 0.55;
const INNER_W = 0.02;
const INNER_MIN_PX = 0.9;
const TICKS = 12;
const TICK_EDGE = 0.78; // tickPhase (1 on a tick line, 0 mid-gap) above which a tick shows
const TICK_SOFT = 0.08;
const TICK_GAP = 0.05; // radial margin from either ring
const TICK_FEATHER = 0.01;
const DISC_TINT = 0.16;
const DISC_EDGE = 0.06;
const CASING_LIGHTEN = 0.55;
const CASING_SCALE = 1.9;
const RING_GLOW = 0.32;
const DISC_GLOW = 0.05;
const AA_PX = 0.75;
const HALF = 0.5;
const FADE_HEIGHT = [120, 260] as const; // camera height (hex radii): full below the first, gone past the second
const TWO_PI = Math.PI * 2;
const WHITE = 1;

/** The rings of a set of actors: city-states with a capital hex, one per hex (first wins). */
export function ringPlan(actors: Iterable<ActorLabel>): CityStateRing[] {
  const byHex = new Map<number, CityStateRing>();

  for (const a of actors) {
    if (!a.cityState || a.capitalHex === undefined || byHex.has(a.capitalHex)) {
      continue;
    }

    byHex.set(a.capitalHex, { index: a.index, hex: a.capitalHex, name: a.name, sources: a.sources });
  }

  return [...byHex.values()];
}

/** Actor index per hex for the atlas (claims.setRings); 0 where no ring stands. */
export function ringCells(rings: readonly CityStateRing[], out: Uint16Array = new Uint16Array(HEX_COUNT)): Uint16Array {
  out.fill(0);
  for (const r of rings) {
    out[r.hex] = r.index;
  }

  return out;
}

/** Rings whose country record carries no source id (truth rule 1). */
export function unsourcedRings(rings: readonly CityStateRing[]): number {
  return rings.filter((r) => r.sources.length === 0).length;
}

/** 1 within `half` of `at`, soft over `aa` (all in the same units as d). */
function band(d: F, at: number, half: F, aa: F): F {
  return smoothstep(half.add(aa), half.sub(aa), abs(d.sub(at)));
}

interface RingShape {
  index: F;
  outer: F;
  casing: F;
  inner: F;
  ticks: F;
  disc: F;
}

export function ringLayer(claims: ClaimField): SurfaceLayer {
  const cache = new WeakMap<object, RingShape>();

  const shape = (ctx: SurfaceCtx): RingShape => {
    const hit = cache.get(ctx.hex);
    if (hit) {
      return hit;
    }

    const index = claims.ringAt(ctx.hex).toVar('ringActor');
    const local = ctx.hex.local;
    const r = length(local);
    const aa = ctx.px.mul(AA_PX);

    // Present only on a ring hex, and fading out as the camera rises.
    const on = index.greaterThan(HALF).select(float(1), float(0));
    const near = smoothstep(FADE_HEIGHT[1], FADE_HEIGHT[0], ctx.camHeight).mul(on);

    const outerW = ctx.px.mul(OUTER_MIN_PX).max(OUTER_W);
    const innerW = ctx.px.mul(INNER_MIN_PX).max(INNER_W);
    const turn = atan(local.y, local.x).div(TWO_PI).mul(TICKS);
    const tickPhase = abs(fract(turn).sub(HALF)).mul(2);
    const radial = smoothstep(INNER_R + TICK_GAP - TICK_FEATHER, INNER_R + TICK_GAP, r).mul(smoothstep(OUTER_R - TICK_GAP + TICK_FEATHER, OUTER_R - TICK_GAP, r));

    const made: RingShape = {
      index,
      outer: band(r, OUTER_R, outerW, aa).mul(near),
      casing: band(r, OUTER_R, outerW.mul(CASING_SCALE), aa).mul(near),
      inner: band(r, INNER_R, innerW, aa).mul(near),
      ticks: smoothstep(TICK_EDGE, TICK_EDGE + TICK_SOFT, tickPhase).mul(radial).mul(near),
      disc: smoothstep(OUTER_R, OUTER_R - DISC_EDGE, r).mul(near),
    };
    cache.set(ctx.hex, made);
    return made;
  };

  const primary = (ctx: SurfaceCtx, s: RingShape): V3 => ctx.data.actorColor(s.index, 0);
  const secondary = (ctx: SurfaceCtx, s: RingShape): V3 => ctx.data.actorColor(s.index, 1);

  return {
    name: 'cityStates',
    order: 320,
    color: (ctx) => {
      const s = shape(ctx);
      const p = primary(ctx, s);
      let c: V3 = mix(ctx.color, p, s.disc.mul(DISC_TINT));
      c = mix(c, mix(p, vec3(WHITE), CASING_LIGHTEN), s.casing);
      c = mix(c, p, s.outer);
      return mix(c, secondary(ctx, s), s.inner.max(s.ticks));
    },
    emissive: (ctx) => {
      const s = shape(ctx);
      const glow = primary(ctx, s).mul(s.outer.mul(RING_GLOW).add(s.disc.mul(DISC_GLOW)));
      return glow.add(secondary(ctx, s).mul(s.inner.max(s.ticks).mul(RING_GLOW * HALF)));
    },
  };
}
